import { AnimatePresence, motion } from 'framer-motion';
import { MessageSquare } from 'lucide-react';
import Spinner from "./spinner";
import { mergeClasses } from '../lib/mergeClasses';
import { useConfig } from '../contexts/config.context';
import React from 'react';

type AgentIntroUIProps = {
  source: string;
  connecting?: boolean;
  onCallNow: (with_audio: boolean) => void;
  setShowChat: (state: boolean) => void;
}

export default function AgentIntroUI({
  source,
  connecting,
  onCallNow,
  setShowChat,
}: AgentIntroUIProps) {
  const { config, updateConfig } = useConfig();
  const [selectedLanguage, setSelectedLanguage] = React.useState<string>(config.remote?.language_settings?.default_language || 'English');

  return (
    <>
      <motion.div
        id="agent-intro"
        initial={{ width: 0, height: 0, scale: 0.3, opacity: 0.2 }}
        animate={{ width: 180, height: 240, scale: 1, opacity: 1 }}
        exit={{ width: 0, height: 0, scale: 0.3, opacity: 0.2 }}
      >
        <div id="agent-intro-container">
          <video
            autoPlay
            loop
            muted
            playsInline
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setShowChat(true)
            }}
          >
            <source src={source}></source>
            Your browser does not support video media.
          </video>
          <div className="absolute bottom-0 left-0 right-0 flex w-full flex-col items-center gap-2 ">
            <div
              className="grid grid-cols-2 w-full gap-2  px-2 pb-2 pt-20 bg-gradient-to-b from-transparent to-70% !to-[var(--sonaris-color-secondary)]"
            >
              {
                config.remote?.language_settings?.supported_languages?.length ?
                  (
                    <div className="!flex !w-full !flex-col !items-center !gap-2 !col-span-full !z-20">
                      <select
                        value={selectedLanguage}
                        onChange={(e) => {
                          setSelectedLanguage(e.target.value);
                          updateConfig({
                            current_language: e.target.value || selectedLanguage
                          })
                        }}
                        className="!text-[10px] !px-2 !py-1 !bg-[var(--sonaris-color-primary)]/50 !text-white !rounded-full !w-full !appearance-none !h-fit"
                      >
                        {[...(new Set([
                          config.remote?.language_settings?.default_language,
                          ...(config.remote?.language_settings?.supported_languages || [])
                        ]))].map((lang) => (
                          <option key={lang} value={lang} className='!bg-[var(--sonaris-color-primary)] !text-black'>
                            {lang}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null
              }
              <AnimatePresence>
                {
                  connecting &&
                  <motion.div className='col-span-full self-center justify-self-center flex items-center justify-center gap-2 text-white text-xs'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Spinner className='w-4 h-4' />Connecting ...
                  </motion.div>
                }
              </AnimatePresence>
              <button
                id="btn-call-now"
                className={mergeClasses('disabled:cursor-not-allowed disabled:opacity-70', {
                  hidden: !config.remote?.character.tts,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  onCallNow(true);
                }}
                disabled={connecting}
              >
                <span className='text-xs font-medium'>{"Let's Talk!"}</span>
              </button>
              <button
                id="btn-chat-now"
                className={mergeClasses('disabled:opacity-70 disabled:cursor-not-allowed ', {
                  '!col-span-full': !config.remote?.character.tts,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  onCallNow(false);
                }}
                disabled={connecting}
              >
                <MessageSquare className='hidden' size={14} />
                <span className='text-xs font-medium'>Chat</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}
